if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        // storecrm再入荷ボタン表示時はsubmitButtonが存在しないためnullチェック
        this.submitButtonText = this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /** 修正: フォームから line item properties[name] を集約（本体行のみに付与） */
      collectLineItemProperties(formData) {
        const props = {};
        const re = /^properties\[(.+)]$/;
        for (const [key, val] of formData.entries()) {
          const match = re.exec(key);
          if (!match || val === '') continue;
          props[match[1]] = typeof val === 'string' ? val : String(val);
        }
        return props;
      }

      /** 修正: 予約販売文言が空のときは line item properties に含めない */
      stripEmptyPreorderProperty(formData) {
        const preorderInput = this.form.querySelector('input[data-preorder-note-input="true"]');
        const noteText = preorderInput?.value.trim() ?? '';
        if (noteText === '') {
          formData.delete('properties[予約販売]');
        }
      }

      /** 修正: メタ option_1〜3 で選択した variant を本体 variant とまとめてカートへ */
      getMetafieldBundleRootEl() {
        const sid = this.dataset.sectionId;
        if (sid) {
          const byId = document.getElementById(`product-metafield-variant-lists-${sid}`);
          if (byId) return byId;
        }
        return document.querySelector('[data-metafield-bundle-root]');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        // submitButtonが存在しない場合（storecrm再入荷ボタン表示中）は処理しない
        if (!this.submitButton) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const bundleRoot =
          this.dataset.metafieldBundleAdd === 'true' ? this.getMetafieldBundleRootEl() : null;
        if (bundleRoot) {
          this.onSubmitMetafieldBundle(evt, bundleRoot);
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        // 修正: metafield 空のとき空 property を Checkout へ送らない
        this.stripEmptyPreorderProperty(formData);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      onSubmitMetafieldBundle(evt, bundleRoot) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const mainIdRaw = formData.get('id');
        if (!mainIdRaw) {
          this.handleErrorMessage('商品を選択できませんでした。');
          this.resetSubmitUiAfterError(evt);
          return;
        }

        const groups = bundleRoot.querySelectorAll('[data-metafield-bundle-group]');
        const addonIds = [];
        for (const group of groups) {
          const checked = group.querySelector(
            'input[type="radio"]:checked:not(:disabled)'
          );
          // 修正: 先頭の「選択しない」(value 空) はセット加算をスキップ
          if (!checked) {
            this.handleErrorMessage('セットのオプションをすべて選択してください。');
            this.resetSubmitUiAfterError(evt);
            return;
          }
          if (!checked.value) {
            continue;
          }
          if (checked.dataset.variantAvailable === 'false') {
            this.handleErrorMessage('選択中のオプションに購入できない商品があります。');
            this.resetSubmitUiAfterError(evt);
            return;
          }
          addonIds.push(checked.value);
        }

        const quantityRaw =
          Number.parseInt(String(formData.get('quantity') || '1'), 10) || 1;
        const quantity = Math.max(1, quantityRaw);
        const mainIdNum = Number.parseInt(String(mainIdRaw), 10);

        /** @type {Map<number, number>} */
        const qtyByVariant = new Map();
        const bump = (idStr, qty) => {
          const vid = Number.parseInt(String(idStr), 10);
          if (!vid) return;
          qtyByVariant.set(vid, (qtyByVariant.get(vid) ?? 0) + qty);
        };

        bump(mainIdRaw, quantity);
        for (const aid of addonIds) bump(aid, quantity);

        const lineProperties = this.collectLineItemProperties(formData);
        const items = [];
        for (const [vid, qty] of qtyByVariant) {
          const line = /** @type {{ id: number; quantity: number; properties?: Record<string, string> }} */ ({
            id: vid,
            quantity: qty,
          });
          if (vid === mainIdNum && Object.keys(lineProperties).length > 0) {
            line.properties = lineProperties;
          }
          items.push(line);
        }

        const payload = /** @type {Record<string, unknown>} */ ({
          items,
          sections_url: window.location.pathname,
        });
        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((s) => s.id).join(',');
          this.cart.setActiveElement(document.activeElement);
        }

        const cartAddJsUrl = `${String(routes.cart_add_url)
          .replace(/\.js$/i, '')
          .replace(/\/$/, '')}.js`;
        fetch(cartAddJsUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: String(mainIdRaw),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(
                response.description || response.message || 'カートへの追加に失敗しました。'
              );
              this.error = true;
              return;
            }
            // 修正: /cart/add.js 成功応答は Cart 全体。cart-notification の renderContents はルートの key（line key）と id を期待する
            if (Array.isArray(response.items) && response.items.length > 0) {
              const mainLine =
                response.items.find((item) => Number(item.variant_id) === mainIdNum) ||
                response.items[response.items.length - 1];
              if (mainLine?.key) {
                response.key = mainLine.key;
              }
              if (mainLine?.variant_id != null) {
                response.id = mainLine.variant_id;
              }
            }
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: String(mainIdRaw),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      /** 修正: バリデーション失敗などで送信前ローディングを戻す */
      resetSubmitUiAfterError(evt) {
        this.error = false;
        this.submitButton.classList.remove('loading');
        this.querySelector('.loading__spinner').classList.add('hidden');
        this.submitButton.removeAttribute('aria-disabled');
        CartPerformance.measureFromEvent("add:user-action", evt);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        // submitButtonが存在しない場合（storecrm再入荷ボタン表示中）は処理しない
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
